import { appStageTotalsHaveValues, sumAppStageCountsForYear } from 'utils/appStageCountsByYear';
import { buildChartYearList, resolveSelectedChartYear, toChartYear } from 'utils/claimChartYears';

/** Snapshot / monthly stage keys that are still in progress (exclude Policy Issued). */
export const IN_PROGRESS_STAGE_KEYS = ['stage01', 'stage02', 'stage02A', 'stage04'];

/**
 * Baseline for left-panel bar widths: max of the four in-progress stages.
 * Policy Issued is shown as a separate count and must not dwarf the pipeline bars.
 */
export function getInProgressStageBaseline(stageCounts = {}) {
  return Math.max(0, ...IN_PROGRESS_STAGE_KEYS.map((key) => Number(stageCounts?.[key] || 0)));
}

/** Width percent for an in-progress stage bar (0–100). */
export function getInProgressStageBarPercent(value, baseline) {
  const numericValue = Number(value) || 0;
  const numericBaseline = Number(baseline) || 0;
  if (numericBaseline <= 0 || numericValue <= 0) return 0;
  return Math.min(100, (numericValue / numericBaseline) * 100);
}

/**
 * Year options for Application Distribution monthly panel.
 * Prefer years present on stage-shaped monthly rows; fall back to claim-chart helper.
 */
export function buildAppDistributionYearList(
  monthlyAggregates,
  currentYear = new Date().getFullYear(),
) {
  const rows = Array.isArray(monthlyAggregates) ? monthlyAggregates : [];
  const stageRows = rows.filter((row) =>
    Object.prototype.hasOwnProperty.call(row || {}, 'stage05'),
  );
  return buildChartYearList(stageRows.length ? stageRows : rows, [{ year: currentYear }]);
}

/**
 * Preferred default year for the monthly panel:
 * current year when it has volume, else latest year with volume, else claim-chart fallback.
 */
export function getPreferredAppDistributionYear(
  yearList,
  monthlyAggregates,
  currentYear = new Date().getFullYear(),
) {
  const options = Array.isArray(yearList) && yearList.length > 0 ? yearList : [currentYear];

  if (options.includes(currentYear)) {
    const currentTotals = sumAppStageCountsForYear(monthlyAggregates, currentYear);
    if (appStageTotalsHaveValues(currentTotals)) {
      return currentYear;
    }
  }

  for (let index = options.length - 1; index >= 0; index -= 1) {
    const year = options[index];
    if (appStageTotalsHaveValues(sumAppStageCountsForYear(monthlyAggregates, year))) {
      return year;
    }
  }

  return resolveSelectedChartYear(options, currentYear);
}

/**
 * Keep a valid selected year; otherwise fall back to the preferred default.
 */
export function resolveAppDistributionYear(
  yearList,
  monthlyAggregates,
  selectedYear,
  currentYear = new Date().getFullYear(),
) {
  const options = Array.isArray(yearList) && yearList.length > 0 ? yearList : [currentYear];
  const selected = toChartYear(selectedYear);

  if (selected != null && options.includes(selected)) {
    return selected;
  }

  return getPreferredAppDistributionYear(yearList, monthlyAggregates, currentYear);
}
